import { z } from '@botpress/sdk'
import { defineConfig } from '@botpress/runtime'
import { ValidationErrors } from './validation-errors'

export type AgentConfig = ReturnType<typeof defineConfig>

// Dependencies schema
export const dependenciesSchema = z.object({
  integrations: z
    .record(
      z.object({
        version: z.string(),
        enabled: z.boolean(), // Whether the integration is enabled
        configurationType: z.string().optional(), // Type of configuration (e.g., "refreshToken", "apiKey")
        connection: z.string().optional(), // e.g., "adk://config/slack/ws/ws_123/default"
        config: z.record(z.any()).optional(), // Local config overrides (non-secret)
      })
    )
    .optional(),
})

export type Dependencies = z.infer<typeof dependenciesSchema>

// Agent deployment info schema
export const agentInfoSchema = z.object({
  botId: z.string().describe('The bot ID from Botpress deployment'),
  workspaceId: z.string().describe('The workspace ID where the bot is deployed'),
  devId: z.string().optional().describe('The development ID used during local development'),
})

export type AgentInfo = z.infer<typeof agentInfoSchema>

// File change event types
export enum FileChangeType {
  Added = 'added',
  Modified = 'modified',
  Deleted = 'deleted',
}

export interface FileChangeEvent {
  type: FileChangeType
  path: string
  relativePath: string
}

// Build event types
export enum BuildEventType {
  Started = 'started',
  Progress = 'progress',
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
}

export interface BuildEvent {
  type: BuildEventType
  message?: string
  error?: Error
  warnings?: string[]
}

// Validation error codes
export enum ValidationErrorCode {
  // Project structure errors
  DIRECTORY_NOT_FOUND = 'DIRECTORY_NOT_FOUND',
  DIRECTORY_ACCESS_ERROR = 'DIRECTORY_ACCESS_ERROR',
  REQUIRED_FILE_MISSING = 'REQUIRED_FILE_MISSING',
  INVALID_STRUCTURE = 'INVALID_STRUCTURE',

  // Configuration errors
  INVALID_CONFIG_SYNTAX = 'INVALID_CONFIG_SYNTAX',
  INVALID_CONFIG_SCHEMA = 'INVALID_CONFIG_SCHEMA',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  AGENT_NOT_LINKED = 'AGENT_NOT_LINKED',

  // Dependencies errors
  INVALID_DEPENDENCIES_SYNTAX = 'INVALID_DEPENDENCIES_SYNTAX',
  INVALID_DEPENDENCIES_SCHEMA = 'INVALID_DEPENDENCIES_SCHEMA',
  INVALID_VERSION_FORMAT = 'INVALID_VERSION_FORMAT',
  UNKNOWN_INTEGRATION = 'UNKNOWN_INTEGRATION',
  INCOMPATIBLE_VERSION = 'INCOMPATIBLE_VERSION',
  CIRCULAR_DEPENDENCY = 'CIRCULAR_DEPENDENCY',

  // File errors
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  INVALID_FILE_NAME = 'INVALID_FILE_NAME',
  DUPLICATE_FILE_NAME = 'DUPLICATE_FILE_NAME',
  DUPLICATE_PRIMITIVE = 'DUPLICATE_PRIMITIVE',
  INVALID_PRIMITIVE_DEFINITION = 'INVALID_PRIMITIVE_DEFINITION',

  // Runtime errors
  BUILD_FAILED = 'BUILD_FAILED',
  SYNTAX_ERROR = 'SYNTAX_ERROR',
  TYPE_ERROR = 'TYPE_ERROR',
  IMPORT_ERROR = 'IMPORT_ERROR',
}

// Validation severity levels
export enum ValidationSeverity {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

// Enhanced validation error interface
export interface ValidationError {
  $type: typeof ValidationErrors.$type
  code: ValidationErrorCode
  severity: ValidationSeverity
  message: string
  file?: string
  line?: number
  column?: number
  hint?: string
  documentation?: string
  context?: Record<string, any>
}

// Validation result with categorized errors
export interface ValidationResult {
  valid: boolean
  errors: ValidationError[]
  warnings: ValidationError[]
  info: ValidationError[]
  errorCount: number
  warningCount: number
  infoCount: number
}

// Agent project state
export enum ProjectState {
  Unloaded = 'unloaded',
  Loading = 'loading',
  Ready = 'ready',
  Building = 'building',
  Error = 'error',
}

// Build output types
export interface BuildOutput {
  files: Map<string, Buffer | string>
  metadata: {
    buildTime: Date
    version?: string
    checksum?: string
  }
}

// Watch options
export interface WatchOptions {
  ignore?: string[]
  debounce?: number
  autoBuild?: boolean
}

// Integration types
export interface Integration {
  name: string
  version: string
  workspace?: string // Optional workspace name
  connection?: string
  config?: Record<string, any>
  installed?: boolean
  installedVersion?: string
  hasChannels?: boolean
}

export interface IntegrationChange {
  type: 'install' | 'update' | 'uninstall'
  integration: Integration
  from?: string // For updates: from version
  to?: string // For updates: to version
}

export interface IntegrationSyncResult {
  changes: IntegrationChange[]
  applied: boolean
  errors?: Error[]
}

// Project info
export interface ProjectInfo {
  path: string
  config: AgentConfig
  dependencies: Dependencies
  agentInfo?: AgentInfo // Optional deployment information
  state: ProjectState
  lastBuildTime?: Date
  errors: ValidationError[]
  warnings: ValidationError[]
  errorCount: number
  warningCount: number
  infoCount: number
  integrations?: Integration[] // Parsed integrations from dependencies
}

export type IntegrationDependency = {
  type: 'integration'
  integration: {
    name: string
    version: string
    enabled: boolean
  }
}

export type InterfaceDependency = {
  type: 'interface'
  interface: {
    name: string
    version: string
  }
}

export type PluginDependency = {
  type: 'plugin'
  plugin: {
    name: string
    version: string
  }
}

export type Dependency = IntegrationDependency | InterfaceDependency | PluginDependency
